import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    Directive,
    ElementRef,
    HostListener,
    inject,
    Input,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PopoverService } from './popover.service';

@Directive({
    selector: '[appPopover]',
    standalone: true,
    providers: [PopoverService],
})
export class PopoverDirective {
    elementRef = inject(ElementRef);
    overlay = inject(Overlay);
    vcr = inject(ViewContainerRef);
    popoverService = inject(PopoverService);
    @Input()
    appPopover!: TemplateRef<object>;

    @Input()
    closeOnClickOutside: boolean = false;

    @Input() content: any;

    @Input() show = true;

    private unsubscribe = new Subject();
    private overlayRef!: OverlayRef;
    private detachTimer: ReturnType<typeof setTimeout>;

    ngOnInit() {
        this.createOverlay();
        this.popoverService.getState().subscribe((resp) => {
            if (resp) {
                this.detachOverlay();
            }
        });
    }

    ngOnDestroy(): void {
        clearTimeout(this.detachTimer);
        this.detachOverlay();
        this.unsubscribe.next(null);
        this.unsubscribe.complete();
    }

    @HostListener('mouseenter') click() {
        if (!this.show) {
            return;
        }
        this.attachOverlay();
    }
    @HostListener('mouseleave') mouseLeave() {
        this.scheduleDetach();
    }

    private createOverlay(): void {
        const scrollStrategy = this.overlay.scrollStrategies.block();
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(
                this.elementRef
            )
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                },
                {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'top',
                },
                {
                    originX: 'end',
                    originY: 'top',
                    overlayX: 'end',
                    overlayY: 'bottom',
                },
            ]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy,
            hasBackdrop: false,
            backdropClass: '',
        });

        this.overlayRef.overlayElement.addEventListener('mouseenter', () => {
            clearTimeout(this.detachTimer);
        });

        this.overlayRef.overlayElement.addEventListener('mouseleave', () => {
            this.scheduleDetach();
        });

        this.overlayRef
            .backdropClick()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(() => {
                if (this.closeOnClickOutside) {
                    this.detachOverlay();
                }
            });
    }

    private scheduleDetach(): void {
        clearTimeout(this.detachTimer);
        this.detachTimer = setTimeout(() => this.detachOverlay(), 150);
    }

    private attachOverlay(): void {
        clearTimeout(this.detachTimer);
        if (!this.overlayRef.hasAttached()) {
            const periodSelectorPortal = new TemplatePortal(
                this.appPopover,
                this.vcr,
                this.content
            );

            this.overlayRef.attach(periodSelectorPortal);
        }
    }

    private detachOverlay(): void {
        if (this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
        }
    }
}
